import { useState, useCallback } from 'react'
import { db } from '@/services/index.ts'
import { useWallet } from './useWallet.ts'

export interface ActivityLogEntry {
  id: string
  wallet_address: string
  vault_id: number | null
  action: string
  details: Record<string, unknown>
  created_at: string
}

export type ActivityAction =
  | 'vault_created'
  | 'vault_encrypted'
  | 'vault_uploaded'
  | 'countdown_started'
  | 'countdown_reset'
  | 'countdown_expired'
  | 'beneficiary_updated'
  | 'contact_updated'
  | 'subscription_created'
  | 'profile_updated'
  | 'vault_accessed'
  | 'notification_sent'

interface UseActivityLogState {
  entries: ActivityLogEntry[]
  loading: boolean
  error: string | null
  hasMore: boolean
}

interface UseActivityLogReturn extends UseActivityLogState {
  fetchLogs: (limit?: number, offset?: number) => Promise<void>
  logActivity: (
    action: ActivityAction,
    vaultId?: number,
    details?: Record<string, unknown>,
  ) => Promise<void>
}

const PAGE_SIZE = 20

export function useActivityLog(): UseActivityLogReturn {
  const { address } = useWallet()

  const [state, setState] = useState<UseActivityLogState>({
    entries: [],
    loading: false,
    error: null,
    hasMore: true,
  })

  const setLoading = () =>
    setState((s) => ({ ...s, loading: true, error: null }))
  const setError = (error: string) =>
    setState((s) => ({ ...s, loading: false, error }))

  const fetchLogs = useCallback(
    async (limit = PAGE_SIZE, offset = 0) => {
      if (!address) return
      setLoading()
      try {
        const { data, error: dbError } = await db.findMany<ActivityLogEntry>(
          'activity_logs',
          {
            filters: [
              { column: 'wallet_address', operator: 'eq', value: address },
            ],
            orderBy: { column: 'created_at', ascending: false },
            limit,
            offset,
          },
        )
        if (dbError) {
          setError(dbError.message)
          return
        }
        const entries = data ?? []
        setState((s) => ({
          ...s,
          entries: offset === 0 ? entries : [...s.entries, ...entries],
          hasMore: entries.length >= limit,
          loading: false,
        }))
      } catch (e) {
        setError(
          e instanceof Error ? e.message : 'Failed to fetch activity logs',
        )
      }
    },
    [address],
  )

  const logActivity = useCallback(
    async (
      action: ActivityAction,
      vaultId?: number,
      details?: Record<string, unknown>,
    ) => {
      if (!address) return
      try {
        await db.insert('activity_logs', {
          wallet_address: address,
          vault_id: vaultId ?? null,
          action,
          details: details ?? {},
        })
      } catch {
        // Activity logging should not break the main flow
      }
    },
    [address],
  )

  return {
    ...state,
    fetchLogs,
    logActivity,
  }
}
